/** Each question is either text (question_name) or an image (question_image) and carries
 *  its own 2–4 typed options; correct_answer must match one of them. option_three /
 *  option_four may be left blank for 2- or 3-option questions. */
export const QUIZ_CSV_HEADERS = [
  "question_image",
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "correct_answer",
];

/** Columns that must be present in the CSV header row (question_image and later options
 *  are optional). */
const QUIZ_CSV_REQUIRED_HEADERS = ["question_image", "option_one", "option_two", "correct_answer"];

export const QUIZ_OPTION_MAX = 100;
export const QUIZ_QUESTION_MAX = 350;
export const QUIZ_IMAGE_NAME_MAX = 255;

/** A question is shown either as text or as an image. */
export function getQuizQuestionType(row) {
  return !String(row?.question_name ?? "").trim() && String(row?.question_image ?? "").trim()
    ? "image"
    : "text";
}

export function quizQuestionHasContent(row) {
  return Boolean(
    String(row?.question_name ?? "").trim() || String(row?.question_image ?? "").trim()
  );
}
export const QUIZ_PAGE_SIZES = [10, 25, 50, 100];

export const QUIZ_OPTION_KEYS = ["option_one", "option_two", "option_three", "option_four"];
export const QUIZ_OPTION_LABELS = ["Option 1", "Option 2", "Option 3", "Option 4"];

export const QUIZ_MIN_OPTIONS = 2;
export const QUIZ_MAX_OPTIONS = 4;
export const QUIZ_OPTION_COUNT_CHOICES = [2, 3, 4];

export function emptyQuizQuestion(id) {
  return {
    id,
    question_name: "",
    question_image: "",
    option_one: "",
    option_two: "",
    option_three: "",
    option_four: "",
    correct_answer: "",
    answer_id: "",
    showOption: QUIZ_MIN_OPTIONS,
  };
}

export const QUIZ_MAX_QUESTIONS = 100;
/** Minimum questions required for the game. */
export const QUIZ_MIN_QUESTIONS = 3;
/** Minimum questions required to persist to the theme database (0 allows clearing incomplete rows). */
export const QUIZ_SAVE_MIN_QUESTIONS = 0;

export const SAMPLE_CSV_ROWS = [
  [
    "diversity_panel.png",
    "Diversity",
    "Equity",
    "Inclusion",
    "",
    "Diversity",
  ],
  [
    "equal_pay.png",
    "Diversity",
    "Equity",
    "Inclusion",
    "Belonging",
    "Equity",
  ],
];

function escapeCsvCell(val) {
  const s = String(val ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildSampleCsvContent() {
  const lines = [
    QUIZ_CSV_HEADERS.join(","),
    ...SAMPLE_CSV_ROWS.map((row) => row.map(escapeCsvCell).join(",")),
  ];
  return lines.join("\r\n");
}

function downloadCsvBlob(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadSampleQuizCsv() {
  downloadCsvBlob(buildSampleCsvContent(), "quiz_questions_sample.csv");
}

export function exportQuizCsv(questions) {
  const rows = (questions || []).map((q) => {
    const c = clampQuizFields(q);
    return [
      c.question_image,
      c.option_one,
      c.option_two,
      c.option_three,
      c.option_four,
      c.correct_answer,
    ]
      .map(escapeCsvCell)
      .join(",");
  });
  const content = [QUIZ_CSV_HEADERS.join(","), ...rows].join("\r\n");
  downloadCsvBlob(content, "quiz_questions_export.csv");
}

/** Parse a single CSV line respecting quoted fields */
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function normalizeHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const HEADER_ALIASES = {
  question_name: "question_name",
  question: "question_name",
  question_text: "question_name",
  question_image: "question_image",
  image: "question_image",
  option_one: "option_one",
  option1: "option_one",
  option_1: "option_one",
  option_two: "option_two",
  option2: "option_two",
  option_2: "option_two",
  option_three: "option_three",
  option3: "option_three",
  option_3: "option_three",
  option_four: "option_four",
  option4: "option_four",
  option_4: "option_four",
  correct_answer: "correct_answer",
  correctanswer: "correct_answer",
  answer: "correct_answer",
};

export const QUIZ_CSV_HEADER_REQUIRED_MESSAGE =
  "CSV cannot be uploaded without a header row. Row 1 must contain: question_image, option_one, option_two, option_three, option_four, correct_answer. question_image is the filename of an image already uploaded for this theme; option_three and option_four may be left blank; correct_answer must exactly match one of that row's options. Download Sample CSV for the correct format.";

function mapHeaderIndex(headers) {
  const index = {};
  headers.forEach((h, i) => {
    const key = HEADER_ALIASES[normalizeHeader(h)];
    if (key && index[key] === undefined) index[key] = i;
  });
  return index;
}

function validateHeaderRow(headerCells) {
  const col = mapHeaderIndex(headerCells);
  const missing = QUIZ_CSV_REQUIRED_HEADERS.filter((k) => col[k] === undefined);
  if (missing.length === QUIZ_CSV_REQUIRED_HEADERS.length) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }
  if (missing.length) {
    throw new Error(
      `Invalid header row. Missing columns: ${missing.join(", ")}. Expected: ${QUIZ_CSV_HEADERS.join(", ")}`
    );
  }
  return col;
}

export function parseQuizCsv(text) {
  const raw = String(text ?? "").replace(/^﻿/, "").trim();
  if (!raw) {
    throw new Error("CSV file is empty.");
  }

  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  const headerCells = parseCsvLine(lines[0]);
  const col = validateHeaderRow(headerCells);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    if (cells.every((c) => !c.trim())) continue;

    const cell = (key) => (col[key] === undefined ? "" : String(cells[col[key]] ?? "").trim());
    const row = {
      question_name: cell("question_name"),
      question_image: cell("question_image"),
      option_one: cell("option_one"),
      option_two: cell("option_two"),
      option_three: cell("option_three"),
      option_four: cell("option_four"),
      correct_answer: cell("correct_answer"),
    };

    if (Object.values(row).every((v) => !v)) continue;
    rows.push(row);
  }

  if (!rows.length) {
    throw new Error("No question rows found in CSV (header row only).");
  }

  return rows;
}

function formatQuizRowMessage(rowLabel, text) {
  return rowLabel ? `${rowLabel} — ${text}` : text;
}

export function getQuizOptionValues(row) {
  return QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim()).filter(Boolean);
}

/** Contiguous filled options from option_one upward (stops at first blank). */
export function getQuizOptionCount(row) {
  let count = 0;
  for (const key of QUIZ_OPTION_KEYS) {
    if (String(row?.[key] ?? "").trim()) count += 1;
    else break;
  }
  return count;
}

export function inferQuizOptionCount(row) {
  const count = getQuizOptionCount(row);
  if (count >= QUIZ_MIN_OPTIONS) return Math.min(QUIZ_MAX_OPTIONS, count);
  return QUIZ_MIN_OPTIONS;
}

/**
 * Rows saved before questions had their own options only carry question_name +
 * correct_answer (a category name). Fill option_one..four from the theme's
 * categories so those rows stay valid — keeping the correct category in the set
 * when the theme has more than 4 categories. Rows that already have options are
 * returned unchanged.
 */
export function fillLegacyQuizOptions(row, categories = []) {
  if (getQuizOptionCount(row) >= QUIZ_MIN_OPTIONS) return row;

  const names = (categories || []).map((c) => String(c?.name ?? "").trim()).filter(Boolean);
  if (names.length < QUIZ_MIN_OPTIONS) return row;

  const picked = names.slice(0, QUIZ_MAX_OPTIONS);
  const answer = String(row?.correct_answer ?? "").trim().toLowerCase();
  const answerName = names.find((n) => n.toLowerCase() === answer);
  if (answerName && !picked.includes(answerName)) {
    picked[picked.length - 1] = answerName;
  }

  const next = { ...row };
  QUIZ_OPTION_KEYS.forEach((key, i) => {
    next[key] = picked[i] || "";
  });
  next.showOption = picked.length;
  return next;
}

/** Clear options beyond `count` and drop correct_answer if it was removed. */
export function applyQuizOptionCount(row, count) {
  const n = Math.min(QUIZ_MAX_OPTIONS, Math.max(QUIZ_MIN_OPTIONS, Number(count) || QUIZ_MIN_OPTIONS));
  const next = { ...row, showOption: n };
  QUIZ_OPTION_KEYS.forEach((key, i) => {
    if (i >= n) next[key] = "";
  });
  const options = getQuizOptionValues(next);
  const answer = String(next.correct_answer ?? "").trim();
  if (answer && !options.some((opt) => opt.toLowerCase() === answer.toLowerCase())) {
    next.correct_answer = "";
  }
  return next;
}

function validateOptionShape(row, rowLabel = "") {
  const vals = QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim());
  let lastFilled = -1;
  for (let i = 0; i < vals.length; i++) {
    if (vals[i]) lastFilled = i;
  }

  if (lastFilled < QUIZ_MIN_OPTIONS - 1) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `At least ${QUIZ_MIN_OPTIONS} options are required (Option 1 and Option 2).`
      ),
    };
  }

  for (let i = 0; i <= lastFilled; i++) {
    if (!vals[i]) {
      return {
        ok: false,
        code: "VALIDATION",
        message: formatQuizRowMessage(
          rowLabel,
          "Options must be filled in order without gaps. Leave later options blank when using only 2 or 3 choices."
        ),
      };
    }
  }

  const seen = new Set();
  for (let i = 0; i <= lastFilled; i++) {
    const key = vals[i].toLowerCase();
    if (seen.has(key)) {
      return {
        ok: false,
        code: "VALIDATION",
        message: formatQuizRowMessage(rowLabel, "Options must all be different."),
      };
    }
    seen.add(key);
  }

  return { ok: true, count: lastFilled + 1 };
}

export function clampQuizFields(row) {
  const questionName = String(
    row?.question_name ?? row?.question ?? row?.question_text ?? ""
  )
    .trim()
    .slice(0, QUIZ_QUESTION_MAX);
  const questionImage = String(row?.question_image ?? "").trim().slice(0, QUIZ_IMAGE_NAME_MAX);

  const option_one = String(row?.option_one ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_two = String(row?.option_two ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_three = String(row?.option_three ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_four = String(row?.option_four ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const correct_answer = String(row?.correct_answer ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const options = [option_one, option_two, option_three, option_four];
  const showFromOptions = options.filter(Boolean).length;
  const showOption = Math.max(
    QUIZ_MIN_OPTIONS,
    Math.min(QUIZ_MAX_OPTIONS, showFromOptions || Number(row?.showOption) || QUIZ_MIN_OPTIONS)
  );
  const answerIndex = options.findIndex(
    (opt) => opt && opt.toLowerCase() === correct_answer.toLowerCase()
  );

  return {
    question_name: questionName,
    question_image: questionImage,
    option_one,
    option_two,
    option_three,
    option_four,
    correct_answer,
    answer_id: answerIndex >= 0 ? String(answerIndex + 1) : String(row?.answer_id ?? "").trim(),
    showOption,
  };
}

const QUIZ_CSV_IMPORT_FIELD_LIMITS = [
  { key: "question_name", label: "Question (question_name)", max: QUIZ_QUESTION_MAX },
  { key: "question_image", label: "Question image (question_image)", max: QUIZ_IMAGE_NAME_MAX },
  ...QUIZ_OPTION_KEYS.map((key, i) => ({
    key,
    label: `${QUIZ_OPTION_LABELS[i]} (${key})`,
    max: QUIZ_OPTION_MAX,
  })),
  { key: "correct_answer", label: "Correct answer (correct_answer)", max: QUIZ_OPTION_MAX },
];

function validateCorrectAnswer(row, rowLabel = "") {
  const options = getQuizOptionValues(row);
  const answer = String(row.correct_answer ?? "").trim();
  if (!answer) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Correct answer is required."),
    };
  }
  const normalizedAnswer = answer.toLowerCase();
  const match = options.find((opt) => opt.toLowerCase() === normalizedAnswer);
  if (!match) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Correct answer must exactly match one of the filled options."
      ),
    };
  }
  return { ok: true, value: match };
}

/** Strict CSV import checks — rejects over-length fields before the usual row validation. */
export function validateQuizRowForImport(row, rowLabel = "Row") {
  for (const { key, label, max } of QUIZ_CSV_IMPORT_FIELD_LIMITS) {
    const value = String(row[key] ?? "").trim();
    if (value.length > max) {
      return {
        ok: false,
        code: "CHAR_LIMIT",
        message: `${rowLabel} — ${label} has ${value.length} characters. Maximum allowed is ${max} characters. Please shorten the text and upload again.`,
      };
    }
  }

  if (!String(row.question_image ?? "").trim()) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "question_image is required."),
    };
  }

  return validateQuizRow({ ...row, question_name: "" }, rowLabel, { requireQuestion: true });
}

export function validateQuizRow(row, rowLabel = "", options = {}) {
  const { requireQuestion = true } = options;
  const normalized = clampQuizFields(row);

  if (requireQuestion && !quizQuestionHasContent(normalized)) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Question text or image is required. Open Edit and add the question text or an image."
      ),
    };
  }

  const shape = validateOptionShape(normalized, rowLabel);
  if (!shape.ok) return shape;

  const answerCheck = validateCorrectAnswer(normalized, rowLabel);
  if (!answerCheck.ok) return answerCheck;

  const q = clampQuizFields({
    ...normalized,
    correct_answer: answerCheck.value,
  });

  return { ok: true, data: q };
}

export function validateQuizList(questions) {
  if (questions.length > QUIZ_MAX_QUESTIONS) {
    return { ok: false, message: `You can save up to ${QUIZ_MAX_QUESTIONS} questions.` };
  }

  if (!questions.length) {
    return { ok: true, data: [] };
  }

  const normalized = [];
  const missingQuestionIndexes = [];
  for (let i = 0; i < questions.length; i++) {
    const result = validateQuizRow(questions[i], `Question ${i + 1}`, {
      requireQuestion: true,
    });
    if (!result.ok) {
      if (/Question text or image is required/i.test(result.message)) {
        missingQuestionIndexes.push(i + 1);
        continue;
      }
      return { ok: false, message: result.message };
    }
    normalized.push(result.data);
  }

  if (missingQuestionIndexes.length) {
    const list = missingQuestionIndexes.join(", ");
    return {
      ok: false,
      message:
        missingQuestionIndexes.length === 1
          ? `Question ${list} is missing its question text or image. Click Edit on that row, add one, then save.`
          : `Questions ${list} are missing their question text or image. Click Edit on each row, add one, then save. You can also Delete incomplete rows and add them again.`,
    };
  }

  return { ok: true, data: normalized };
}
